import type { RouteHandle } from '@/app/document-title';
import { RootLayout } from '@/app/root-layout';
import { RouteErrorPage } from '@/app/route-error-page';
import { OperatorCallbackPage } from '@/auth/callback-page';
import { OperatorLoginPage } from '@/auth/login-page';
import { PropertyAccessGuard } from '@/auth/property-access-guard';
import { operatorConfig, operatorSession } from '@/auth/runtime';
import type { OperatorSession } from '@/auth/session';
import { AccessPropertyDetailPage, AccessPropertyListPage } from '@/pages/access/access-page';
import { NativeAccessPage } from '@/pages/access/native-access-page';
import { BulkPage } from '@/pages/bulk/bulk-page';
import { BookingDetailPage } from '@/pages/contracts/booking-detail-page';
import { BookingListPage } from '@/pages/contracts/booking-list-page';
import type { BookingRuntime } from '@/pages/contracts/booking-queries';
import { DashboardPage } from '@/pages/dashboard/dashboard-page';
import { EditorPage } from '@/pages/editor/editor-page';
import { EventCalendarPage } from '@/pages/event-calendar/event-calendar-page';
import { InlineEditPage } from '@/pages/inline-edit/inline-edit-page';
import { DetailPageScreen } from '@/pages/list/detail-page';
import { ListPageScreen } from '@/pages/list/list-page';
import { MasterDetailPage } from '@/pages/master-detail/master-detail-page';
import { MatrixPage } from '@/pages/matrix/matrix-page';
import { NotFoundPage } from '@/pages/not-found-page';
import { PermissionTreePage } from '@/pages/permission-tree/permission-tree-page';
import { NativePropertyReadPage } from '@/pages/property/native-property-read-page';
import { PropertyDetailPage } from '@/pages/property/property-detail-page';
import type { PropertyRuntime } from '@/pages/property/property-queries';
import { ServerListPage } from '@/pages/server-list/server-list-page';
import { AdvancedTab } from '@/pages/settings/advanced-tab';
import { GeneralTab } from '@/pages/settings/general-tab';
import { NotificationsTab } from '@/pages/settings/notifications-tab';
import { SettingsIndexRedirect, SettingsLayout } from '@/pages/settings/settings-layout';
import { SortablePage } from '@/pages/sortable/sortable-page';
import { SpreadsheetPage } from '@/pages/spreadsheet/spreadsheet-page';
import { StayDetailPage } from '@/pages/stays/stay-detail-page';
import { StayListPage } from '@/pages/stays/stay-list-page';
import { stayRuntime, type StayRuntime } from '@/pages/stays/stay-queries';
import { TimelinePage } from '@/pages/timeline/timeline-page';
import { UserDetailPage } from '@/pages/users/user-detail-page';
import { UserListPage } from '@/pages/users/user-list-page';
import type { UserRuntime } from '@/pages/users/user-queries';
import { BasicStep } from '@/pages/wizard/basic-step';
import { DetailsStep } from '@/pages/wizard/details-step';
import { OptionsStep } from '@/pages/wizard/options-step';
import { ReviewStep } from '@/pages/wizard/review-step';
import { WizardCompletePage } from '@/pages/wizard/wizard-complete-page';
import { WizardIndexRedirect, WizardLayout } from '@/pages/wizard/wizard-layout';
import { Navigate, type RouteObject } from 'react-router';

// 데이터 라우터(createBrowserRouter) — 이탈 가드(useBlocker)가 데이터 라우터에서만 동작한다.
// 업무 메뉴와 개발 데모 메뉴는 app/nav.ts에서 분리한다. handle.title은 브라우저 탭 제목이 된다.
const h = (title: RouteHandle['title']): RouteHandle => ({ title });

export function createRoutes(
  session: OperatorSession = operatorSession,
  propertyRuntime?: PropertyRuntime,
  bookingRuntime?: BookingRuntime,
  staysRuntime: StayRuntime = stayRuntime,
  userRuntime?: UserRuntime,
): RouteObject[] {
  return [
    {
      path: 'login',
      element: <OperatorLoginPage session={session} />,
      handle: h('운영자 로그인'),
    },
    {
      path: operatorConfig
        ? new URL(operatorConfig.redirectUri).pathname.slice(1)
        : 'auth/callback',
      element: <OperatorCallbackPage />,
      handle: h('운영자 로그인'),
    },
    {
      element: <RootLayout session={session} />,
      // 화면 하나가 렌더 중 실패해도 사이드바는 남기고 본문에만 안내를 띄운다.
      children: [
        {
          errorElement: <RouteErrorPage />,
          children: [
            { index: true, element: <Navigate to="/properties" replace /> },
            {
              element: <PropertyAccessGuard session={session} />,
              children: [
                {
                  path: 'properties',
                  element: <NativePropertyReadPage runtime={propertyRuntime} />,
                  handle: h('지점'),
                },
                {
                  path: 'properties/:propertyId',
                  element: <NativePropertyReadPage runtime={propertyRuntime} />,
                  handle: h('지점 상세'),
                },
                {
                  path: 'properties/legacy/:propertyId',
                  element: <PropertyDetailPage runtime={propertyRuntime} enableServerSorting />,
                  handle: h('기존 지점 상세'),
                },
              ],
            },
            {
              element: (
                <PropertyAccessGuard
                  session={session}
                  domain="property"
                  requiredBits={1}
                  areaName="출입 관리"
                  indexPath="/access"
                  indexLabel="지점 선택"
                />
              ),
              children: [
                {
                  path: 'access',
                  element: <NativeAccessPage runtime={propertyRuntime} />,
                  handle: h('출입 관리'),
                },
                {
                  path: 'access/legacy',
                  element: <AccessPropertyListPage runtime={propertyRuntime} />,
                  handle: h('기존 지점 출입 설정'),
                },
                {
                  path: 'access/native/:propertyId',
                  element: <NativeAccessPage runtime={propertyRuntime} />,
                  handle: h('출입 관리'),
                },
                {
                  path: 'access/:propertyId',
                  element: <AccessPropertyDetailPage runtime={propertyRuntime} />,
                  handle: h('출입 관리'),
                },
              ],
            },
            {
              element: (
                <PropertyAccessGuard
                  session={session}
                  domain="user"
                  requiredBits={2}
                  areaName="사용자 관리"
                  indexPath="/users"
                  indexLabel="사용자 목록으로"
                />
              ),
              children: [
                {
                  path: 'users',
                  element: <UserListPage runtime={userRuntime} />,
                  handle: h('사용자 관리'),
                },
                {
                  path: 'users/:userId',
                  element: <UserDetailPage runtime={userRuntime} />,
                  handle: h('사용자 상세'),
                },
              ],
            },
            {
              element: (
                <PropertyAccessGuard
                  session={session}
                  domain="booking"
                  requiredBits={2}
                  areaName="개인 예약 현황"
                  indexPath="/contracts"
                  indexLabel="예약 목록으로"
                />
              ),
              children: [
                {
                  path: 'contracts',
                  element: <BookingListPage runtime={bookingRuntime} enableServerSorting />,
                  handle: h('개인 예약 현황'),
                },
                {
                  path: 'contracts/:bookingId',
                  element: <BookingDetailPage runtime={bookingRuntime} />,
                  handle: h('개인 예약 상세'),
                },
              ],
            },
            {
              element: (
                <PropertyAccessGuard
                  session={session}
                  domain="booking"
                  requiredBits={2}
                  areaName="투숙 현황"
                  indexPath="/stays"
                  indexLabel="투숙 목록으로"
                />
              ),
              children: [
                {
                  path: 'stays',
                  element: <StayListPage runtime={staysRuntime} enableServerSorting />,
                  handle: h('투숙 현황'),
                },
                { path: 'stays/:stayId', element: <StayDetailPage />, handle: h('투숙 상세') },
              ],
            },
            // 로그인·콜백을 뺀 나머지 화면(개발 데모, 없는 주소)도 인증 경계 안에 둔다. 도메인 권한은 요구하지 않는다.
            {
              element: (
                <PropertyAccessGuard
                  session={session}
                  domain="operator"
                  requiredBits={0}
                  areaName="업무 화면"
                  indexPath="/properties"
                  indexLabel="지점 목록으로"
                />
              ),
              children: [
                // DEV는 Vite 개발 서버에서만 true다. production 빌드에는 데모 경로를 등록하지 않는다.
                ...(import.meta.env.DEV
                  ? ([
                      {
                        path: 'dev',
                        handle: h('개발 데모'),
                        children: [
                          { index: true, element: <DashboardPage />, handle: h('대시보드') },
                          // 목록
                          { path: 'list', element: <ListPageScreen />, handle: h('데이터 리스트') },
                          {
                            path: 'list/:recordId',
                            element: <DetailPageScreen />,
                            handle: h((params) => `${params.recordId ?? ''} · 데이터 리스트`),
                          },
                          {
                            path: 'master-detail',
                            element: <MasterDetailPage />,
                            handle: h('리스트 + 패널'),
                          },
                          { path: 'bulk', element: <BulkPage />, handle: h('일괄 처리') },
                          {
                            path: 'server-list',
                            element: <ServerListPage />,
                            handle: h('서버 연동 목록'),
                          },
                          { path: 'sortable', element: <SortablePage />, handle: h('순서 편집') },
                          // 입력·편집
                          {
                            path: 'inline-edit',
                            element: <InlineEditPage />,
                            handle: h('인라인 편집 표'),
                          },
                          {
                            path: 'spreadsheet',
                            element: <SpreadsheetPage />,
                            handle: h('스프레드시트 뷰'),
                          },
                          {
                            path: 'settings',
                            element: <SettingsLayout />,
                            handle: h('설정'),
                            children: [
                              { index: true, element: <SettingsIndexRedirect /> },
                              { path: 'general', element: <GeneralTab />, handle: h('일반') },
                              {
                                path: 'notifications',
                                element: <NotificationsTab />,
                                handle: h('알림'),
                              },
                              { path: 'advanced', element: <AdvancedTab />, handle: h('고급') },
                            ],
                          },
                          {
                            path: 'wizard',
                            element: <WizardLayout />,
                            handle: h('다단계 위저드'),
                            children: [
                              { index: true, element: <WizardIndexRedirect /> },
                              { path: 'basic', element: <BasicStep />, handle: h('기본 정보') },
                              { path: 'details', element: <DetailsStep />, handle: h('상세 정보') },
                              { path: 'options', element: <OptionsStep />, handle: h('옵션') },
                              {
                                path: 'review',
                                element: <ReviewStep />,
                                handle: h('검토 및 제출'),
                              },
                              {
                                path: 'complete',
                                element: <WizardCompletePage />,
                                handle: h('완료'),
                              },
                            ],
                          },
                          {
                            path: 'editor',
                            element: <EditorPage />,
                            handle: h('편집기 + 미리보기'),
                          },
                          {
                            path: 'permission-tree',
                            element: <PermissionTreePage />,
                            handle: h('권한 트리'),
                          },
                          // 보기
                          { path: 'timeline', element: <TimelinePage />, handle: h('타임라인 뷰') },
                          {
                            path: 'event-calendar',
                            element: <EventCalendarPage />,
                            handle: h('이벤트 캘린더'),
                          },
                          { path: 'matrix', element: <MatrixPage />, handle: h('매트릭스·히트맵') },
                        ],
                      },
                    ] satisfies RouteObject[])
                  : []),
                { path: '*', element: <NotFoundPage />, handle: h('페이지를 찾을 수 없음') },
              ],
            },
          ],
        },
      ],
    },
  ];
}
